import React from 'react';
import { useDispatch } from 'react-redux';
import ButtonLoader from '../../../components/Layout/Buttons/ButtonLoader';
import { useStateSelector } from '../../../store/selectors';
import { ConfirmClose } from '../../../slices/marketplace/closing-date/thunks/confirm-close.thunk';
import { ClosingDateCloseConfirmation } from '../../../slices/marketplace/closing-date/models/engagement-closing-date.model';
import {
    TimelineParty,
    getPartyName,
} from '../../../slices/marketplace/timeline/models/enums/timeline-party.enum';

const PARTY_DISPLAY_ORDER = [TimelineParty.Buyer, TimelineParty.Seller];

export interface ClosingDateCloseConfirmationSectionProps {
    engagementId: number;
    closeConfirmations: ClosingDateCloseConfirmation[];
    currentUserParty?: TimelineParty | null;
    canConfirmClose: boolean;
    hasClosingDateArrived: boolean;
}

export const ClosingDateCloseConfirmationSection = ({
    engagementId,
    closeConfirmations,
    currentUserParty,
    canConfirmClose,
    hasClosingDateArrived,
}: ClosingDateCloseConfirmationSectionProps) => {
    const dispatch = useDispatch() as any;
    const isSubmitting = useStateSelector(
        (state) => state.closingDateState.isSubmitting
    );

    const handleConfirmClose = () => {
        if (isSubmitting) {
            return;
        }
        dispatch(ConfirmClose(engagementId));
    };

    const orderedConfirmations = PARTY_DISPLAY_ORDER.map((party) =>
        closeConfirmations.find((confirmation) => confirmation.party === party)
    ).filter(
        (confirmation): confirmation is ClosingDateCloseConfirmation =>
            !!confirmation
    );

    return (
        <div className="closing-date-close-confirmation">
            <div className="closing-date-close-confirmation__title">
                Confirm transaction closed
            </div>
            <div className="closing-date-close-confirmation__description">
                When you understand the transaction to be closed, please confirm
                below. Closing should be confirmed after all executed documents
                have been released from escrow and all agreed payments as
                documented in the flow of funds have commenced. The status of
                each payment can be confirmed by buyer and seller in the flow of
                funds tab.
            </div>
            <div className="closing-date-close-confirmation__panels">
                {orderedConfirmations.map((confirmation) => {
                    const isYou = confirmation.party === currentUserParty;
                    return (
                        <div
                            key={confirmation.party}
                            className={`closing-date-close-confirmation__panel${
                                confirmation.isConfirmed
                                    ? ' closing-date-close-confirmation__panel--confirmed'
                                    : ''
                            }`}>
                            <div className="closing-date-close-confirmation__panel-info">
                                <div className="closing-date-close-confirmation__panel-label">
                                    {getPartyName(confirmation.party)}
                                    {isYou && (
                                        <span className="closing-date-close-confirmation__panel-you">
                                            {' '}
                                            (you)
                                        </span>
                                    )}
                                </div>
                                <div className="closing-date-close-confirmation__panel-status">
                                    {confirmation.isConfirmed
                                        ? 'Confirmed close'
                                        : 'Awaiting confirmation'}
                                </div>
                            </div>
                            {confirmation.isConfirmed ? (
                                <span className="badge badge-pill badge-success">
                                    Confirmed
                                </span>
                            ) : isYou ? (
                                <ButtonLoader
                                    buttonText="Confirm close"
                                    loaderButtonText="Confirm close"
                                    isLoading={isSubmitting}
                                    disabled={isSubmitting || !canConfirmClose}
                                    onClick={handleConfirmClose}
                                    className="btn btn-primary"
                                />
                            ) : hasClosingDateArrived ? (
                                <span className="badge badge-pill badge-warning">
                                    Pending
                                </span>
                            ) : null}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
